<template>
  <div class="login-panel">
    <div class="login-panel-main">
      <div class="login-title">
        {{ $t('login.welcome') }}
      </div>
      <SwitchMode
        v-model="mode"
        :data="[
          { value: 'email', label: $t('login.use-email-login') },
          { value: 'phone', label: $t('login.use-phone-login') }
        ]"
      />
      <PasswordLogin
        ref="passwordLoginPanel"
        :mode="mode"
        :error="error"
        :loading="loading"
        :is-show-verify="isShowVerify"
        @login="login"
        @error="handleErrors"
      />
      <GeetestCheck
        ref="geetest"
        target=".panel"
        @validate-pass="geetestPass"
      />
      <div class="form-footer">
        <a href class="register" @click.prevent="showRegister">
          {{ $t('login.register') }}
        </a>
        <a
          :href="forgetPasswordUrl | langParam"
          target="_blank"
          class="forgetPass"
        >
          {{ $t('login.forgetPass') }}
        </a>
      </div>
    </div>
    <TwoStepVerify
      v-if="isShowTwoStep"
      :type="'login'"
      :params="twoStepParams"
      @closeTwoStep="closeTwoStep"
    />
    <NoticeBox ref="notice" />
    <ConfirmBox ref="confirm" />
  </div>
</template>
<script>
import Icon from 'com/Icon'
import PasswordLogin from './PasswordLogin'
import { ERROR_CODE } from 'const/common'
import { MODIFY_EMAIL_PWD_LINK, MODIFY_PHONE_PWD_LINK } from 'const/link'
import bus from 'src/eventbus'
import TwoStepVerify from '../TwoStepVerify'
import SwitchMode from '../SwitchMode'
import NoticeBox from '../NoticeBox'
import ConfirmBox from '../ConfirmBox'
import GeetestCheck from 'com/GeetestCheck'

export default {
  name: 'LoginPanel',
  components: {
    ConfirmBox,
    SwitchMode,
    TwoStepVerify,
    NoticeBox,
    Icon,
    GeetestCheck,
    PasswordLogin
  },
  provide() {
    return {
      $panel: this
    }
  },
  props: {
    redirect: {
      type: Object,
      required: false
    }
  },
  watch: {
    mode() {
      const loginPanel = this.$refs.passwordLoginPanel
      this.isShowVerify = false
      this.isShowTwoStep = false
      loginPanel && loginPanel.clearErrors()
    }
  },
  data: () => ({
    mode: 'email',
    error: null,
    loading: false,
    isShowVerify: false,
    isShowRegister: false,
    isShowTwoStep: false,
    fromParam: {},
    twoStepParams: null
  }),
  computed: {
    forgetPasswordUrl() {
      return this.mode === 'phone'
        ? MODIFY_PHONE_PWD_LINK
        : MODIFY_EMAIL_PWD_LINK
    }
  },
  created() {
    this.$track('login_page_view')
    bus.on(bus.$events.INTERCEPT_MESSAGE, () => {
      this.reset()
    })
  },
  methods: {
    geetestPass(data) {
      this.fromParam.apiParams = { ...this.fromParam.apiParams, ...data }
      this.loginReq()
    },
    login(apiParams, isFast = false) {
      if (this.loading) return
      this.fromParam = { apiParams, isFast }
      this.loginReq()
    },
    loginReq() {
      const { isFast, apiParams } = this.fromParam
      const action = isFast ? 'userFastLogin' : 'userLogin'
      this.loading = true
      this.error = null
      this.$store
        .dispatch(action, apiParams)
        .then(() => {
          this.handleSuccess()
        })
        .catch(error => {
          this.handleErrors(error, { ...apiParams, action })
        })
    },
    handleSuccess() {
      this.reset()
      if (this.$route.query.redirect || this.redirect) {
        this.$router.push(this.$route.query.redirect || this.redirect)
      }
    },
    handleErrors(error, params) {
      this.loading = false

      if (!error) return
      // 需要通过两步验证
      const errorCode = error.error_code
      if (errorCode === ERROR_CODE.OTP_EMPTY) {
        this.isShowTwoStep = true
        this.twoStepParams = params
        return
      }
      // 操作次数太多
      if (errorCode === ERROR_CODE.PASSWORD_TRY_MAX) {
        this.showConfirm({
          content: error.error_description,
          hasCancel: false
        })
        return
      }
      if (ERROR_CODE.NEED_GEETEST_CODES.includes(errorCode)) {
        // 图片验证改极验
        this.$refs.geetest.init()
        return
      }
      if (
        [ERROR_CODE.NEW_DEVICE_PHONE, ERROR_CODE.NEW_DEVICE_EMAIL].includes(
          errorCode
        )
      ) {
        this.$track('login_device_verify')
        this.isShowVerify = true
      }
      this.error = error
    },
    showRegister() {
      this.$emit('showRegister')
    },
    closeTwoStep() {
      this.isShowTwoStep = false
      this.isShowVerify = false
    },
    showNotice(...args) {
      this.$refs.notice.show(...args)
    },
    showConfirm(props) {
      this.$refs.confirm.show(props)
    },
    clearErrors() {
      const loginPanel = this.$refs.passwordLoginPanel
      loginPanel && loginPanel.clearErrors()
    },
    reset() {
      const loginPanel = this.$refs.passwordLoginPanel
      this.mode = 'email'
      this.error = null
      this.twoStepParams = null
      this.isShowTwoStep = false
      this.isShowVerify = false
      this.loading = false
      loginPanel && loginPanel.reset()
    }
  }
}
</script>
<style lang="less" scoped>
@import '~uikit';

.login-panel {
  width: 340px;
  position: relative;
  padding: 30px 45px 30px;
  color: @black;
  .login-panel-overlay {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: white;
  }
}
.tiger-logo {
  margin-bottom: 30px;
  text-align: center;

  img {
    display: inline-block;
    height: 60px;
  }
}

.form-footer {
  overflow: hidden;
  a {
    float: right;
    margin-top: 15px;
    color: @light-grey;
    &:first-of-type {
      float: left;
      color: @blue-sky;
      &:hover,
      &:focus {
        color: @blue-sky;
      }
    }
    &:hover,
    &:focus {
      color: var(--login-active-color);
    }
  }
}
.login-title {
  font-size: 24px;
  line-height: 34px;
  text-align: center;
  margin-bottom: 30px;
  white-space: nowrap;
}
</style>
